import React, { useState, useMemo, useEffect } from 'react';
import styled from '@emotion/styled';
import { Color } from './bitmap';
import { useList, useKeyPress, useKeyPressEvent } from 'react-use';
import { SketchPicker, RGBColor } from 'react-color';


export interface PaletteProps {
  color: Color,
  size?: number,
  onChangeColor: (color: Color) => void,
}

export function usePalette(defaultColor: Color = Color.black): PaletteProps {
  const [color, onChangeColor] = useState<Color>(defaultColor);
  return { color, onChangeColor }
}

export function Palette(props: PaletteProps) {
  const {
    color,
    onChangeColor,
  } = props;
  const size = props.size ?? 32;
  const defaultColorList = [
    new Color(0, 0, 0, 1),
    new Color(0, 0, 0, 0),
    new Color(255, 255, 255, 1),
    new Color(255, 0, 0, 1),
    new Color(0, 0, 255, 1),
    new Color(0, 255, 0, 1),
    new Color(190, 32, 54, 1),
    new Color(12, 129, 23, 1),
    new Color(12, 193, 41, 1),
    new Color(12, 32, 209, 1),
  ]

  useEffect(() => onChangeColor(defaultColorList[0].clone()), []);

  const [colorList, { updateAt }] = useList<Color>(defaultColorList);
  const activeColorIndex = useMemo(() => {
    if (colorList.length === 0 || color === undefined) {
      return undefined;
    } else {
      return colorList.findIndex(c => c && color.eq(c))
    }
  }, [colorList, color]);

  const onClickHandle = useMemo(() => (idx: number) => () => {
    if (colorList[idx]) {
      if (idx === activeColorIndex) {
        return;
      } else {
        onChangeColor(colorList[idx]);
      }
    }
  }, [colorList, activeColorIndex]);

  const keyDown = useMemo(() => (e: KeyboardEvent) => {
    if (e.key === '0') {
      onClickHandle(9)();
    } else {
      onClickHandle(+e.key - 1)();
    }
  }, [onClickHandle])

  const [isChangeColorModialVisible] = useKeyPress(e => e.key === '`' || e.key === '·');
  useKeyPressEvent(e => '1234567890'.split('').includes(e.key), keyDown);

  const {
    Container,
    ColorBlock,
    ColorBlockContainer,
    KeyBoardTip,
  } = S;
  return (
    <Container
      style={{
        gridTemplateColumns: `repeat(10,${size}px)`,
      }}
    >
      {colorList.map((cl, idx) => (
        <ColorBlockContainer
          key={idx}
          onClick={onClickHandle(idx)}
          style={{
            width: size,
          }}
        >
          <ColorBlock
            key={idx}
            color={cl ? cl.rgba : undefined}
            style={{
              width: size,
              height: size,
            }}
          />
          <KeyBoardTip id="tip" acitve={idx === activeColorIndex}>{(idx + 1) % 10}</KeyBoardTip>
        </ColorBlockContainer>
      ))}
      {
        isChangeColorModialVisible ? (
          <div
            style={{
              position: 'absolute',
              top: 'calc(50% - 150px)',
              left: 'calc(50% - 100px)',
              zIndex: 1000,
            }}
          >
            <SketchPicker
              color={colorList[activeColorIndex]}
              onChange={res => {
                const { r, g, b, a } = res.rgb;
                updateAt(activeColorIndex, new Color(r, g, b, a));
                onChangeColor(new Color(r, g, b, a));
              }}
            />
          </div>
        ) : null
      }
    </Container>
  )
}

const S = {
  Container: styled.div`
    display: grid;
  `,
  ColorBlock: styled.div<{ color?: string }>`
    background-color: ${props => props.color ?? '#fff'};
  `,
  ColorBlockContainer: styled.div`
    text-align: center;
    cursor: pointer;
    &:hover > #tip {
      line-height: 6px;
      border: solid 1px blue;
    }
  `,
  KeyBoardTip: styled.div<{ acitve?: boolean }>`
    font-size: 8px;
    line-height: ${props => props.acitve ? '6px' : '8px'};
    border: ${props => props.acitve ? 'solid 1px red' : 'none'};
    padding: 4px 0;
    user-select: none;
  `,
};
